/**
 * Perks Service
 * Manages partner rewards and achievements
 */

import { ref, onValue, off} from 'firebase/database';
import { database } from './firebase/core';
import { getData, setData} from './firebase/api-wrapper.service';
import { Perk, PerkReward, UnlockedPerk, PERK_UNLOCK_CONDITIONS } from '../types/achievements';
import logger from '../utils/logger';

class PerksService {
  private perksCache: Map<string, Perk> = new Map();
  private rewardsCache: Map<string, PerkReward> = new Map();
  private listeners: Map<string, () => void> = new Map();

  /**
   * Get all available perks
   */
  async getAllPerks(): Promise<Perk[]> {
    try {
      const perksData = await getData('perks/list');
      if (perksData) {
        const perks = Object.entries(perksData).map(([key, data]) => ({
          key,
          ...(data as Omit<Perk, 'key'>),
        }));
        
        // Update cache
        perks.forEach(perk => this.perksCache.set(perk.key, perk));
        
        // Sort by orderIndex
        return perks.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
      }
      return [];
    } catch (error) {
      logger.error('Error fetching perks:', error);
      return [];
    }
  }

  /**
   * Get a specific perk
   */
  async getPerk(perkKey: string): Promise<Perk | null> {
    // Check cache first
    if (this.perksCache.has(perkKey)) {
      return this.perksCache.get(perkKey)!;
    }

    try {
      const perkData = await getData(`perks/list/${perkKey}`);
      if (perkData) {
        const perk = { key: perkKey, ...perkData } as Perk;
        this.perksCache.set(perkKey, perk);
        return perk;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching perk:', error);
      return null;
    }
  }

  /**
   * Get user's unlocked perks
   */
  async getUserUnlockedPerks(uid: string): Promise<UnlockedPerk[]> {
    try {
      const unlockedData = await getData(`perks/unlockedPerks/${uid}`);
      if (unlockedData) {
        return Object.entries(unlockedData).map(([perkKey, data]) => ({
          perkKey,
          ...(data as Omit<UnlockedPerk, 'perkKey'>),
        }));
      }
      return [];
    } catch (error) {
      logger.error('Error fetching unlocked perks:', error);
      return [];
    }
  }

  /**
   * Check if user has unlocked a specific perk
   */
  async isPerkUnlocked(uid: string, perkKey: string): Promise<boolean> {
    try {
      const unlockedData = await getData(`perks/unlockedPerks/${uid}/${perkKey}`);
      return !!unlockedData;
    } catch (error) {
      logger.error('Error checking perk unlock status:', error);
      return false;
    }
  }

  /**
   * Unlock a perk for a user
   */
  async unlockPerk(uid: string, perkKey: string): Promise<boolean> {
    try {
      // Check if already unlocked
      const isUnlocked = await this.isPerkUnlocked(uid, perkKey);
      if (isUnlocked) {
        logger.info(`Perk ${perkKey} already unlocked for user ${uid}`);
        return true;
      }

      // Check if perk exists and is active
      const perk = await this.getPerk(perkKey);
      if (!perk || !perk.isActive) {
        logger.warn(`Perk ${perkKey} not found or inactive`);
        return false;
      }

      // Check if perk is still valid
      if (perk.validUntil && perk.validUntil < Date.now()) {
        logger.warn(`Perk ${perkKey} has expired`);
        return false;
      }

      // Unlock the perk
      const unlockData: UnlockedPerk = {
        perkKey,
        unlockedAt: Date.now(),
        hasSeenReward: false,
        copiedCode: false,
      };

      await setData(`perks/unlockedPerks/${uid}/${perkKey}`, unlockData);
      logger.info(`Perk ${perkKey} unlocked for user ${uid}`);
      
      // Trigger perk unlock notification
      if ((window as any).__showAchievement) {
        (window as any).__showAchievement({
          id: `perk-${perkKey}`,
          name: perk.title,
          description: perk.teaser,
          category: 'special',
          tier: 'gold',
          points: 0,
          iconUrl: perk.imageURL,
          unlockedAt: Date.now(),
          type: 'perk'
        });
      }
      
      return true;
    } catch (error) {
      logger.error('Error unlocking perk:', error);
      return false;
    }
  }

  /**
   * Get reward for a perk
   */
  async getPerkReward(perkKey: string): Promise<PerkReward | null> {
    // Check cache first
    if (this.rewardsCache.has(perkKey)) {
      return this.rewardsCache.get(perkKey)!;
    }

    try {
      const rewardData = await getData(`perks/rewards/${perkKey}`);
      if (rewardData) {
        const reward = rewardData as PerkReward;
        this.rewardsCache.set(perkKey, reward);
        return reward;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching perk reward:', error);
      return null;
    }
  }

  /**
   * Mark perk reward as seen
   */
  async markRewardSeen(uid: string, perkKey: string): Promise<void> {
    try {
      await setData(`perks/unlockedPerks/${uid}/${perkKey}/hasSeenReward`, true);
    } catch (error) {
      logger.error('Error marking reward as seen:', error);
    }
  }

  /**
   * Mark perk code as copied
   */
  async markCodeCopied(uid: string, perkKey: string): Promise<void> {
    try {
      await setData(`perks/unlockedPerks/${uid}/${perkKey}/copiedCode`, true);
    } catch (error) {
      logger.error('Error marking code as copied:', error);
    }
  }

  /**
   * Unlock perks for new users
   */
  async unlockNewUserPerks(uid: string): Promise<void> {
    const newUserPerks = PERK_UNLOCK_CONDITIONS.NEW_USER_PERKS;
    
    for (const perkKey of newUserPerks) {
      await this.unlockPerk(uid, perkKey);
    }
  }

  /**
   * Check and unlock "10 for 10" perk
   */
  async check10For10Perk(uid: string, friendCount: number): Promise<boolean> {
    const perkKey = 'TEN_FOR_TEN';
    
    // Check if already unlocked
    const isUnlocked = await this.isPerkUnlocked(uid, perkKey);
    if (isUnlocked) {
      return false;
    }

    // Check if friend count meets requirement
    if (friendCount >= PERK_UNLOCK_CONDITIONS.TEN_FOR_TEN.minFriends) {
      const unlocked = await this.unlockPerk(uid, perkKey);
      if (unlocked) {
        logger.info(`10 for 10 perk unlocked for user ${uid}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Subscribe to perks updates
   */
  subscribeToPerks(callback: (perks: Perk[]) => void): () => void {
    const perksRef = ref(database, 'perks/list');
    
    const handleUpdate = (snapshot: any) => {
      if (snapshot.exists()) {
        const perksData = snapshot.val();
        const perks = Object.entries(perksData).map(([key, data]) => ({
          key,
          ...(data as Omit<Perk, 'key'>),
        }));
        
        // Update cache
        perks.forEach(perk => this.perksCache.set(perk.key, perk));
        
        // Sort and return
        callback(perks.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      } else {
        callback([]);
      }
    };

    onValue(perksRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(perksRef, 'value', handleUpdate);
    };
  }

  /**
   * Subscribe to user's unlocked perks
   */
  subscribeToUserPerks(uid: string, callback: (unlockedPerks: UnlockedPerk[]) => void): () => void {
    const userPerksRef = ref(database, `perks/unlockedPerks/${uid}`);
    
    const handleUpdate = (snapshot: any) => {
      if (snapshot.exists()) {
        const unlockedData = snapshot.val();
        const unlockedPerks = Object.entries(unlockedData).map(([perkKey, data]) => ({
          perkKey,
          ...(data as Omit<UnlockedPerk, 'perkKey'>),
        }));
        callback(unlockedPerks);
      } else {
        callback([]);
      }
    };

    onValue(userPerksRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(userPerksRef, 'value', handleUpdate);
    };
  }

  /**
   * Clean up all listeners
   */
  cleanup(): void {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
    this.perksCache.clear();
    this.rewardsCache.clear();
  }
}

export const perksService = new PerksService();
export default perksService;